/* eslint-disable */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Clock,
  Settings2,
  XCircle,
  CheckCircle2,
  AlertCircle,
  Unlink,
  Star,
  Zap,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type IntegrationStatus =
  | "connect"
  | "connected"
  | "not_connected"
  | "requires_configuration"
  | "error"
  | "pending";

export interface IntegrationCardButton {
  label: string;
  path: string;
  variant?: "primary" | "secondary";
}

export interface IntegrationCardConfig {
  /** Card heading */
  title: string;
  /** Subheading/category shown below the title */
  subtitle?: string;
  /** Short description shown below the title */
  description: string;
  /** Connection / health status — drives auto button logic */
  status?: IntegrationStatus;
  /** When true, shows a Coming Soon ribbon and disables all buttons */
  comingSoon?: boolean;
  /** When true, shows a Featured badge next to the title */
  featured?: boolean;
  /**
   * Note shown under description.
   * Auto-set to "Requires configuration" (not_connected) or
   * "Configured workspace" (connected) when omitted.
   */
  note?: string;

  // ── Smart button paths ────────────────────────────────────────────────────
  /**
   * Path for the "Connect" action (not_connected) or "Disconnect" (connected).
   * When provided, the left outline button is auto-rendered.
   */
  connectPath?: string;
  /**
   * Path for the primary "Configure →" button shown when status = not_connected.
   */
  configurePath?: string;
  /**
   * Path for the primary "View details →" button shown when status = connected.
   */
  detailsPath?: string;

  /** Override the default "View details" label when connected */
  primaryLabelConnected?: string;
  /** Override the default "Configure" label when not connected */
  primaryLabelNotConnected?: string;

  // ── Manual override ───────────────────────────────────────────────────────
  /** Pass explicit buttons to override status-based auto logic entirely. */
  buttons?: IntegrationCardButton[];

  /** Lucide icon component displayed next to the title */
  icon?: React.ElementType;
  /** Explicit initials to display in the avatar */
  initials?: string;
  /** Explicit color to use for the avatar background (e.g. hex code) */
  color?: string;
}

interface IntegrationCardGridProps {
  cards: IntegrationCardConfig[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const getInitialsFallback = (title: string) => {
  const words = title.trim().split(/\s+/);
  let initials = "";
  if (words.length > 0) {
    if (words.length >= 2) {
      initials = (words[0][0] + words[1][0]).toUpperCase();
    } else {
      initials = words[0].slice(0, 2);
      if (initials.length > 0) {
        initials =
          initials[0].toUpperCase() +
          (initials[1] ? initials[1].toLowerCase() : "");
      }
    }
  }
  return initials || "IN";
};

// ─────────────────────────────────────────────────────────────────────────────
// Single Card
// ─────────────────────────────────────────────────────────────────────────────

const IntegrationCard: React.FC<IntegrationCardConfig> = (props) => {
  const {
    title,
    subtitle,
    description,
    status,
    comingSoon = false,
    featured = false,
    icon: CardIcon,
    initials: propInitials,
    color,
    detailsPath,
    configurePath,
    connectPath,
  } = props;
  const navigate = useNavigate();

  // Local status allows toggling connect/disconnect without a backend call
  const [localStatus, setLocalStatus] = useState<IntegrationStatus | undefined>(
    status,
  );

  React.useEffect(() => {
    setLocalStatus(status);
  }, [status]);

  const isConnected = localStatus?.toLowerCase() === "connect" || localStatus?.toLowerCase() === "connected";

  const handleConnectClick = () => {
    if (comingSoon || !connectPath) return;
    navigate(connectPath);
  };

  const handleConfigureClick = () => {
    if (comingSoon) return;
    const path = configurePath ?? detailsPath;
    if (path) navigate(path);
  };

  const ResolvedIcon = CardIcon;
  const displayInitials =
    propInitials || (!ResolvedIcon ? getInitialsFallback(title) : null);
  const bgStyle = color ? { backgroundColor: color } : undefined;
  const bgClass = !color ? "bg-[#5850ec]" : "";

  return (
    <div
      className={`relative flex flex-col rounded-[24px] border bg-white shadow-sm transition-all duration-200 overflow-hidden
        ${
          comingSoon
            ? "border-dashed border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-white opacity-80"
            : "border-gray-200/80 hover:shadow-md hover:-translate-y-[1px]"
        }`}
    >
      {/* ── Coming Soon corner ribbon ─────────────────────────────────────── */}
      {comingSoon && (
        <div className="absolute top-0 right-0 z-10 overflow-hidden w-[88px] h-[88px] pointer-events-none">
          <div
            className="absolute top-[20px] right-[-20px] w-[88px] text-center text-[9px] font-bold
                        uppercase tracking-widest bg-indigo-500 text-white py-[5px] shadow rotate-45"
          >
            Soon
          </div>
        </div>
      )}

      {/* ── Card body ────────────────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 p-6 gap-4">
        {/* Title row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-4">
            {/* Avatar / Profile Icon */}
            <div
              className={`flex items-center justify-center w-14 h-14 rounded-2xl font-bold text-white text-lg shrink-0 select-none ${bgClass}`}
              style={bgStyle}
            >
              {displayInitials ? (
                displayInitials
              ) : ResolvedIcon ? (
                <ResolvedIcon className="w-6 h-6 text-white" />
              ) : (
                "IN"
              )}
            </div>

            {/* Title & Subtitle */}
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2 pr-6">
                <h3
                  className={`text-lg font-bold leading-tight
                    ${comingSoon ? "text-gray-400" : "text-gray-900"}`}
                >
                  {title}
                </h3>
                {featured && !comingSoon && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md bg-[#f0f2fe] text-[#5850ec] border border-indigo-50">
                    <Star className="w-3 h-3 text-[#5850ec] fill-[#5850ec]" />
                    Featured
                  </span>
                )}
              </div>
              {subtitle && (
                <p className="text-xs font-semibold text-gray-400">
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Status Badge */}
          {comingSoon ? (
            <span className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 shrink-0">
              Coming Soon
            </span>
          ) : localStatus ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border border-gray-200/80 bg-gray-50/50 text-gray-500 shrink-0">
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  localStatus?.toLowerCase() === "connect" || localStatus?.toLowerCase() === "connected"
                    ? "bg-emerald-500"
                    : "bg-gray-400"
                }`}
              />
              {localStatus === "not_connected"
                ? "Not Connected"
                : localStatus === "connect" || localStatus === "connected"
                  ? "Connected"
                  : localStatus}
            </span>
          ) : null}
        </div>

        {/* Description */}
        <p
          className={`text-sm leading-relaxed font-medium flex-1 ${
            comingSoon ? "text-gray-400" : "text-gray-500"
          }`}
        >
          {description}
        </p>

        {/* Alert box — driven by connection status */}
        {!comingSoon && (
          <>
            {!isConnected ? (
              <div className="border border-dashed border-[#d5deff] bg-[#f4f7ff]/70 rounded-2xl p-4 flex gap-3 items-center">
                <div className="w-8 h-8 rounded-full bg-[#e8edff] flex items-center justify-center text-[#5850ec] shrink-0">
                  <Zap
                    className="w-4 h-4 text-[#5850ec] fill-[#5850ec]"
                    strokeWidth={2.5}
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-gray-900 leading-tight">
                    Setup required
                  </span>
                  <span className="text-xs font-semibold text-[#5850ec] mt-0.5">
                    Connect to start accepting payments — takes ~60 seconds.
                  </span>
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-emerald-200 bg-emerald-50/30 rounded-2xl p-4 flex gap-3 items-center">
                <div className="w-8 h-8 rounded-full bg-emerald-100/80 flex items-center justify-center text-emerald-600 shrink-0">
                  <CheckCircle2 className="w-4 h-4 fill-emerald-100" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-gray-900 leading-tight">
                    Integration Active
                  </span>
                  <span className="text-xs font-semibold text-emerald-600 mt-0.5">
                    SMART360 is syncing data in real-time.
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {/* Button row */}
        <div className="mt-2 pt-2 flex items-center justify-between">
          {/* View Details — disabled when not connected */}
          <button
            disabled={comingSoon || !isConnected}
            onClick={() => {
              const p = detailsPath ?? configurePath;
              if (p) navigate(p);
            }}
            className={`font-semibold text-sm transition-colors focus:outline-none
              ${
                !comingSoon && isConnected
                  ? "text-[#5850ec] hover:text-[#4f46e5] cursor-pointer"
                  : "text-gray-300 cursor-not-allowed"
              }`}
          >
            {props.primaryLabelConnected || "View Details"}
          </button>

          {/* Connect (not connected) → redirects | Configure (connected) → navigates to configure */}
          {isConnected ? (
            <button
              disabled={comingSoon}
              onClick={handleConfigureClick}
              className="font-bold text-sm px-5 py-2.5 rounded-xl transition-all shadow-sm bg-[#5850ec] hover:bg-[#4f46e5] text-white focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#5850ec] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Configure
            </button>
          ) : (
            <button
              disabled={comingSoon || !connectPath}
              onClick={handleConnectClick}
              className="font-bold text-sm px-5 py-2.5 rounded-xl transition-all shadow-sm bg-[#5850ec] hover:bg-[#4f46e5] text-white focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#5850ec] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {props.primaryLabelNotConnected || "+ Connect"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Grid wrapper
// ─────────────────────────────────────────────────────────────────────────────

export const IntegrationCardGrid: React.FC<IntegrationCardGridProps> = ({
  cards,
}) => {
  if (!cards || cards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2">
        <Settings2 className="w-8 h-8" />
        <p className="text-sm">No integrations configured yet.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {cards.map((card, idx) => (
        <IntegrationCard key={card.title + idx} {...card} />
      ))}
    </div>
  );
};

export default IntegrationCardGrid;
